/**
 * Geocode a free-form address to a lat/lng via OpenStreetMap Nominatim — the
 * same backend the in-map search (`AddressSearch`) uses, so we keep zero extra
 * deps and no API keys in the public bundle (see coding guidelines rule 10).
 *
 * Why this exists: the funnel only ever stores the address as text
 * (street / house / zip / city — see STORAGE_KEYS), never coordinates. To open
 * the result-page map centred on the user's actual street we resolve that text
 * to a point once, on demand, through `useQuery`.
 */
export interface GeoPoint {
  lat: number;
  lng: number;
}

export async function geocodeAddress(
  query: string,
  locale: string,
): Promise<GeoPoint | null> {
  const q = query.trim();
  if (q.length < 4) return null;
  const params = new URLSearchParams({
    format: "json",
    limit: "1",
    countrycodes: "de",
    q,
  });
  const res = await fetch(
    `https://nominatim.openstreetmap.org/search?${params.toString()}`,
    { headers: { "Accept-Language": locale } },
  );
  const data = (await res.json()) as Array<{ lat: string; lon: string }>;
  const hit = Array.isArray(data) ? data[0] : undefined;
  if (!hit) return null;
  const lat = Number(hit.lat);
  const lng = Number(hit.lon);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}
